import React from "react";
import { ProgressIndicator, Stack, Text } from "@fluentui/react";
import styles from "./FileProgressBar.module.css";

interface FileProgressBarProps {
  fileName: string;
  progress: number;
  status: "uploading" | "completed" | "error" | "pending" | "processing";
  error?: string;
  onViewFile?: () => void;
  onDelete?: () => void;
  processingMessage?: string;
  timeRemaining?: number;
  elapsedTime?: number;
  estimatedTotalTime?: number;
  creationTime?: Date;
}

const FileProgressBar: React.FC<FileProgressBarProps> = ({
  fileName,
  progress,
  status,
  error,
  onViewFile,
  onDelete,
  processingMessage,
  timeRemaining,
  elapsedTime,
  estimatedTotalTime,
  creationTime,
}) => {
  const formatTime = (seconds: number): string => {
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    } else {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return `${minutes}m ${remainingSeconds}s`;
    }
  };

  const formatTimestamp = (date: Date): string => {
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${hours}:${minutes} ${day}/${month}/${year}`;
  };

  const getProgressLabel = () => {
    switch (status) {
      case "uploading":
        return `Uploading... ${Math.round(progress)}%`;
      case "completed":
        return `Ready to use - uploaded at ${formatTimestamp(creationTime || new Date())}`;
      case "processing":
        let label = processingMessage || "Processing embeddings...";
        if (
          estimatedTotalTime &&
          elapsedTime &&
          elapsedTime > estimatedTotalTime
        ) {
          const extraTime = elapsedTime - estimatedTotalTime;
          if (extraTime >= 60) {
            label += ` (Slower than usual)`;
          } else {
            label += ` (${formatTime(extraTime)} slower than usual)`;
          }
        } else if (timeRemaining !== undefined && timeRemaining > 0) {
          label += ` (${formatTime(timeRemaining)} remaining)`;
        }
        return label;
      case "error":
        return error || "Upload failed";
      case "pending":
        return "Pending upload";
      default:
        return "";
    }
  };

  const getProgressBarClassName = () => {
    switch (status) {
      case "completed":
        return styles.progressBarCompleted;
      case "error":
        return styles.progressBarError;
      case "uploading":
      case "processing":
        return styles.progressBarUploading;
      default:
        return "";
    }
  };

  return (
    <div className={styles.fileProgressContainer}>
      <div className={styles.fileHeader}>
        <a
          className={
            status === "completed" ? styles.clickableFileName : styles.fileName
          }
          onClick={status === "completed" ? onViewFile : undefined}
          title={fileName}
        >
          {fileName}
        </a>
        {status === "completed" && onDelete && (
          <button
            onClick={onDelete}
            className={styles.deleteButton}
            aria-label={`Delete ${fileName}`}
          >
            Delete
          </button>
        )}
      </div>
      <ProgressIndicator
        percentComplete={
          status === "processing" &&
          timeRemaining !== undefined &&
          estimatedTotalTime
            ? // Calculate progress from 30% to 95% based on time remaining
              Math.min(
                0.95,
                Math.max(
                  0.3,
                  ((estimatedTotalTime - timeRemaining) / estimatedTotalTime) *
                    0.85 +
                    0.3,
                ),
              )
            : status === "processing"
              ? 0.3 // Always start processing at 30%
              : status === "uploading"
                ? Math.min(0.3, progress / 100) // Cap upload progress at 30%
                : progress / 100
        }
        description={getProgressLabel()}
        className={getProgressBarClassName()}
      />
    </div>
  );
};

export default FileProgressBar;
